import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: PEGN, ERP = Portal ERP, NV = Novo Varejo, BAG = Baguete).
// Ângulo: a dívida que já virou perda (antiga, pequena, negativada). A régua
// do dia a dia é assunto de /cobranca-whatsapp — não disputar essa keyword.
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";
const SOURCE_NV_BRUNO = "Bruno Reis, fundador, ao Novo Varejo, ago/2026";

export const recuperacaoDeCredito: SeoLandingConfig = {
  path: "/recuperacao-de-credito",
  kind: "ferramenta",
  keyword: "recuperação de crédito para varejo",
  title: "Recuperação de crédito para varejo pelo WhatsApp",
  description:
    "Recuperação de crédito para varejo: a Fidexa cobra pelo WhatsApp dívidas antigas e negativadas de qualquer valor, e o devedor quita por Pix na própria conversa.",
  breadcrumbLabel: "Recuperação de crédito",
  updatedAt: "2026-10-17",
  service: {
    name: "Recuperação de crédito para o varejo",
    serviceType:
      "Recuperação de dívidas em atraso e negativadas do crediário próprio via WhatsApp",
    audience: "Lojas e redes de varejo com carteira de crediário em atraso",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Recuperação de dívidas do crediário",
      title:
        "Recuperação de crédito para varejo: a dívida dada como perdida volta ao caixa",
      lead: "Aquela parcela antiga, pequena demais para um processo e já negativada, ainda tem dono. A Fidexa leva a cobrança ao WhatsApp do devedor, sua equipe negocia pelo chat e o pagamento entra por Pix.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Como a recuperação acontece",
      secondaryHref: "#como-funciona",
      reassurance: "Demonstração gratuita com um especialista.",
    },
    {
      type: "proof",
      title: "249 empresas cobram com a Fidexa. Algumas delas:",
      clientNames: [
        "Lojas Elder",
        "Tangará",
        "Grupo Minas",
        "Lojas Skala",
        "Omara Calçados",
        "SuperModas",
      ],
    },
    {
      type: "shortAnswer",
      id: "o-que-e",
      title: "O que é recuperação de crédito no varejo",
      answer:
        "Recuperação de crédito no varejo é trazer de volta ao caixa o dinheiro de vendas parceladas que o cliente deixou de pagar, inclusive dívidas antigas e negativadas. Na Fidexa, isso acontece pelo WhatsApp: mensagens automáticas retomam o contato com o devedor, a equipe da loja negocia o acordo no chat da Fidexa e o pagamento chega por Pix.",
      bullets: [
        "Dívidas de qualquer valor, até as de R$ 50 que um processo judicial não paga.",
        "Também cobra quem já está nos órgãos de proteção ao crédito.",
        "Recupera a parcela e os juros, com baixa automática.",
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "Da carteira parada ao dinheiro no caixa",
      lead: "O foco aqui é o que já saiu da cobrança normal: o atraso longo, a dívida esquecida, o cliente negativado.",
      steps: [
        {
          title: "Separe o que está parado",
          text: "Envie as dívidas antigas e em atraso, negativadas ou não, pela integração com o TOTVS Linx ou por planilha com devedor, valor e data de vencimento.",
        },
        {
          title: "Retomada do contato",
          text: "O devedor recebe uma mensagem no WhatsApp pela API oficial, em template aprovado pela Meta, com a pendência e o convite para resolver.",
        },
        {
          title: "Acordo que cabe no bolso",
          text: "Quem responde querendo parcelar conversa com a equipe da loja no chat da Fidexa, que reúne os números de WhatsApp num lugar só. O acordo sai ali.",
        },
        {
          title: "Recebido e de volta à loja",
          text: "O devedor escolhe a parcela e paga por Pix no Fidexa Pay, sem ajuda de ninguém, e a baixa acontece sozinha. Quem quita a dívida tende a voltar a comprar.",
        },
      ],
    },
    {
      type: "comparison",
      id: "alternativas",
      title: "Dar como perda, levar à Justiça ou recuperar com a Fidexa",
      lead: "O que acontece com a mesma dívida antiga em cada caminho.",
      caption:
        "Comparação entre deixar a dívida do crediário como perda, cobrá-la na Justiça e recuperá-la com a Fidexa",
      columns: [
        { key: "perda", label: "Deixar como perda" },
        { key: "judicial", label: "Cobrança judicial" },
        { key: "fidexa", label: "Fidexa", highlight: true },
      ],
      rows: [
        {
          label: "Dívida pequena, de R$ 50 ou menos",
          cells: {
            perda: "Fica para trás",
            judicial: "O custo do processo supera a dívida",
            fidexa: "Entra na cobrança",
          },
        },
        {
          label: "Dívida já negativada",
          cells: {
            perda: "Segue parada",
            judicial: "Só com processo",
            fidexa: "Cobrada pelo WhatsApp",
          },
        },
        {
          label: "Quem conduz",
          cells: {
            perda: "Ninguém",
            judicial: "Um processo na Justiça",
            fidexa: "Régua automática e a equipe da loja no chat",
          },
        },
        {
          label: "Acordo negociado pelo WhatsApp",
          cells: { perda: false, judicial: false, fidexa: true },
        },
        {
          label: "Pagamento por Pix dentro da conversa",
          cells: { perda: false, judicial: false, fidexa: true },
        },
      ],
    },
    {
      type: "metrics",
      id: "numeros",
      title: "Recuperação em números",
      metrics: [
        {
          value: "R$ 39,34 mi",
          label:
            "Recuperados para lojistas: R$ 34 mi em parcelas e R$ 5,34 mi em juros",
          source: SOURCE_ERP_NV,
        },
        {
          value: "32,10%",
          label: "Taxa média de recuperação da carteira",
          source: "Plataforma Fidexa",
        },
        {
          value: "20%",
          label:
            "Dos créditos tidos como perdidos, recuperados já no primeiro mês",
          source: SOURCE_NV_BRUNO,
        },
      ],
    },
    {
      type: "faq",
      id: "perguntas",
      title: "Perguntas sobre recuperação de crédito para varejo",
      items: [
        {
          question: "O que é recuperação de crédito para varejo?",
          answer:
            "É o trabalho de receber as vendas a prazo que o cliente deixou de pagar, principalmente as dívidas antigas que a loja já considera perdidas. Na Fidexa, o contato, o acordo e o pagamento acontecem no WhatsApp, sem processo judicial.",
        },
        {
          question: "Vale a pena tentar recuperar uma dívida de R$ 50?",
          answer:
            "Vale, desde que o custo da cobrança seja menor que a dívida. Na Justiça, o processo costuma custar mais do que valores pequenos. A Fidexa cobra dívidas de qualquer valor de forma automática, então a conta fecha.",
        },
        {
          question: "Dá para recuperar dívida de cliente negativado?",
          answer:
            "Dá. A Fidexa também é usada para recuperar dívidas que a loja já encaminhou aos órgãos de proteção ao crédito. O devedor recebe a proposta no WhatsApp e pode pagar por Pix na hora.",
        },
        {
          question: "Quanto da dívida antiga a Fidexa consegue recuperar?",
          answer:
            "A taxa média de recuperação da carteira na plataforma Fidexa é de 32,10%. Sobre os créditos que os lojistas consideravam perdidos, o fundador Bruno Reis afirmou ao Novo Varejo que a plataforma costuma recuperar 20% já no primeiro mês.",
        },
        {
          question: "Quanto a Fidexa já recuperou para o varejo?",
          answer:
            "R$ 39,34 milhões em menos de um ano de operação, sendo R$ 34 milhões em parcelas e recebíveis e R$ 5,34 milhões em juros, segundo o Portal ERP e o Novo Varejo.",
        },
        {
          question: "Os juros da dívida também são recuperados?",
          answer:
            "Sim. Do total recuperado pela Fidexa, R$ 5,34 milhões são juros, além dos R$ 34 milhões em parcelas.",
        },
        {
          question: "O cliente que quita a dívida volta a comprar na loja?",
          answer:
            "Tende a voltar. Segundo Bruno Reis, fundador da Fidexa, ao Novo Varejo, as pessoas que pagam a dívida tendem a voltar a ser compradoras ativas do lojista.",
        },
        {
          question: "A cobrança da dívida antiga constrange o cliente?",
          answer:
            "Não precisa constranger. As mensagens seguem o modelo de template aprovado pela Meta, a negociação fica com a equipe da própria loja e o devedor paga por Pix pelo Fidexa Pay no momento que preferir.",
        },
        {
          question:
            "Qual a diferença entre recuperação de crédito e cobrança via WhatsApp?",
          answer:
            "A recuperação trata o que já virou prejuízo: atraso longo, dívida negativada, cliente que sumiu. A cobrança via WhatsApp do dia a dia cuida da parcela que ainda vai vencer ou acabou de vencer. A Fidexa faz as duas, e as duas partem da mesma carteira.",
        },
        {
          question: "Como as dívidas antigas entram na Fidexa?",
          answer:
            "Pela integração com o ERP TOTVS Linx ou por planilha com os dados dos clientes, os valores devidos e as datas de vencimento. A loja não precisa trocar o sistema de gestão.",
        },
        {
          question: "Quanto custa a recuperação de crédito com a Fidexa?",
          answer:
            "O valor depende do tamanho da operação. Agende a demonstração gratuita: um especialista avalia a sua carteira em atraso e apresenta a proposta.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "cta-final",
      title: "Transforme a dívida parada em caixa",
      text: "Mostre a carteira em atraso a um especialista na demonstração gratuita e veja quanto dela pode voltar.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
